/**
 * Resolve a numeric amount and ISO currency code from Mews multi-currency price maps.
 * Label always matches the bucket key used (no mixing currency code with another key’s value).
 *
 * @see docs/room booking page/display currency implementation.md — Phase 1
 */

/** One currency block under `Price.TotalAmount` or product `Pricing.Value`. */
export interface MewsGrossAmountBlock {
  Currency?: string;
  GrossValue?: number;
  [key: string]: unknown;
}

/** Room/rate `Price` object from hotels/getAvailability (and similar). */
export interface MewsMultiCurrencyPrice {
  Total?: Record<string, number | undefined>;
  TotalAmount?: Record<string, MewsGrossAmountBlock | undefined>;
  [key: string]: unknown;
}

export interface PickDisplayAmountOptions {
  /** Hint when neither display nor CHF exists (e.g. settlement / rate currency). */
  settlementCurrencyHint?: string;
}

export interface PickedDisplayAmount {
  value: number;
  currencyCode: string;
}

function normCode(code: string): string {
  return code.trim().toUpperCase();
}

function isFiniteNumber(n: unknown): n is number {
  return typeof n === "number" && Number.isFinite(n);
}

function grossFromBlock(block: unknown): number | null {
  if (!block || typeof block !== "object") return null;
  const g = (block as MewsGrossAmountBlock).GrossValue;
  return isFiniteNumber(g) ? g : null;
}

function totalNumberForCode(
  total: Record<string, number | undefined> | undefined,
  codeUpper: string
): number | null {
  if (!total) return null;
  const direct = total[codeUpper];
  if (isFiniteNumber(direct)) return direct;
  for (const k of Object.keys(total)) {
    if (normCode(k) === codeUpper && isFiniteNumber(total[k])) {
      return total[k]!;
    }
  }
  return null;
}

function grossFromTotalAmountMap(
  map: Record<string, MewsGrossAmountBlock | undefined> | undefined,
  codeUpper: string
): number | null {
  if (!map) return null;
  const direct = grossFromBlock(map[codeUpper]);
  if (direct != null) return direct;
  for (const k of Object.keys(map)) {
    if (normCode(k) === codeUpper) {
      const g = grossFromBlock(map[k]);
      if (g != null) return g;
    }
  }
  return null;
}

/**
 * For a single ISO key, prefer `Total[code]` when it is a finite number, else `TotalAmount[code].GrossValue`.
 */
function amountForCode(
  price: MewsMultiCurrencyPrice,
  codeUpper: string
): number | null {
  const t = totalNumberForCode(price.Total, codeUpper);
  if (t != null) return t;
  return grossFromTotalAmountMap(price.TotalAmount, codeUpper);
}

/** ISO codes present on a Mews `Price` (from `Total` / `TotalAmount`). */
export function collectCurrencyKeysFromPrice(
  price: MewsMultiCurrencyPrice | null | undefined
): string[] {
  if (!price || typeof price !== "object") return [];

  const keys = new Set<string>();

  const total = price.Total;
  if (total && typeof total === "object") {
    for (const k of Object.keys(total)) {
      if (isFiniteNumber(total[k])) keys.add(normCode(k));
    }
  }

  const ta = price.TotalAmount;
  if (ta && typeof ta === "object") {
    for (const k of Object.keys(ta)) {
      if (grossFromBlock(ta[k]) != null) keys.add(normCode(k));
    }
  }

  return [...keys];
}

function buildPreferenceOrder(
  displayCurrency: string,
  options: PickDisplayAmountOptions | undefined,
  availableKeys: string[]
): string[] {
  const preferred: string[] = [];
  const push = (c: string | undefined) => {
    if (!c) return;
    const n = normCode(c);
    if (n && !preferred.includes(n)) preferred.push(n);
  };

  push(displayCurrency);
  push("CHF");
  push(options?.settlementCurrencyHint);

  const rest = availableKeys
    .map(normCode)
    .filter((k) => k && !preferred.includes(k))
    .sort();

  return [...preferred, ...rest];
}

/**
 * Pick amount for browse/rate pricing: `Total[code]` or `TotalAmount[code].GrossValue`.
 * Fallback order: displayCurrency → CHF → settlementCurrencyHint (if any) → other keys (sorted).
 */
export function pickDisplayAmount(
  price: MewsMultiCurrencyPrice | null | undefined,
  displayCurrency: string,
  options?: PickDisplayAmountOptions
): PickedDisplayAmount | null {
  if (!price || typeof price !== "object") return null;

  const keys = collectCurrencyKeysFromPrice(price);
  if (keys.length === 0) return null;

  const order = buildPreferenceOrder(displayCurrency, options, keys);

  for (const code of order) {
    const value = amountForCode(price, code);
    if (value != null) {
      return { value, currencyCode: code };
    }
  }

  return null;
}

export type ProductPricingValueMap = Record<string, MewsGrossAmountBlock | undefined>;

/**
 * Pick a product amount in the same ISO as the room (or guest) display total.
 *
 * When this product omits `referenceCurrencyCode` on `Pricing.Value`, `pickProductPricingAmount` falls
 * back to CHF and would no longer match the room’s BOB/EUR line. For **variable-count** add-ons, Mews
 * often exposes full multi-currency on the **base** product only; relative rows (2nd–4th person) may be
 * CHF-only. In that case we derive: `relChf * (baseDisplay / baseChf)` using the base product’s own
 * CHF ↔ display amounts — same implicit FX Mews already uses for the base (no external FX).
 */
export function pickProductPricingAlignedToReference(
  valueMap: ProductPricingValueMap | null | undefined,
  displayCurrency: string,
  referenceCurrencyCode: string,
  options: PickDisplayAmountOptions | undefined,
  baseValueMapForChfRatio: ProductPricingValueMap | null | undefined,
): PickedDisplayAmount | null {
  if (!valueMap || typeof valueMap !== "object") return null;

  const ref = normCode(referenceCurrencyCode);
  const direct = pickProductPricingAmount(valueMap, displayCurrency, options);
  if (direct && normCode(direct.currencyCode) === ref) {
    return direct;
  }

  if (!baseValueMapForChfRatio || typeof baseValueMapForChfRatio !== "object") {
    return null;
  }

  const baseDisplay = pickProductPricingAmount(baseValueMapForChfRatio, displayCurrency, options);
  const baseChf = pickProductPricingAmount(baseValueMapForChfRatio, "CHF", options);
  const relChf = pickProductPricingAmount(valueMap, "CHF", options);

  if (
    !baseDisplay ||
    !baseChf ||
    !relChf ||
    baseChf.value <= 1e-9 ||
    normCode(baseDisplay.currencyCode) !== ref
  ) {
    return null;
  }

  return {
    value: relChf.value * (baseDisplay.value / baseChf.value),
    currencyCode: baseDisplay.currencyCode,
  };
}

/**
 * Product / upsell `Pricing.Value`-shaped map: keyed by ISO code, values carry `GrossValue`.
 * Same fallback discipline as `pickDisplayAmount`.
 */
export function pickProductPricingAmount(
  valueMap: ProductPricingValueMap | null | undefined,
  displayCurrency: string,
  options?: PickDisplayAmountOptions
): PickedDisplayAmount | null {
  if (!valueMap || typeof valueMap !== "object") return null;

  const byUpper: Record<string, MewsGrossAmountBlock | undefined> = {};
  for (const k of Object.keys(valueMap)) {
    const block = valueMap[k];
    if (grossFromBlock(block) == null) continue;
    byUpper[normCode(k)] = block;
  }

  const availableKeys = Object.keys(byUpper);
  if (availableKeys.length === 0) return null;

  const order = buildPreferenceOrder(displayCurrency, options, availableKeys);

  for (const code of order) {
    const g = grossFromBlock(byUpper[code]);
    if (g != null) {
      return { value: g, currencyCode: code };
    }
  }

  return null;
}
